// client/src/pages/admin/CompanySettings.tsx
import React, { useEffect } from 'react';
import {
  Card,
  Form,
  Input,
  Button,
  Space,
  Row,
  Col,
  Select,
  InputNumber,
  Upload,
  message,
  Divider,
  Typography,
  Alert,
  TimePicker,
  Tag,
  Spin
} from 'antd';
import {
  SaveOutlined,
  UploadOutlined,
  ClockCircleOutlined,
  DollarOutlined,
  SafetyOutlined,
  GlobalOutlined,
  PhoneOutlined,
  MailOutlined,
  HomeOutlined
} from '@ant-design/icons';
import { useAuthStore } from '../../stores/authStore';
import { tenantService } from '../../services/tenantService';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';

const { Text } = Typography;
const { Option } = Select;
const { TextArea } = Input;

const AdminCompanySettings: React.FC = () => {
  useAuthStore(); // For future use
  const [companyForm] = Form.useForm();
  const [businessForm] = Form.useForm();
  const [financialForm] = Form.useForm();
  const [alertsForm] = Form.useForm();
  const queryClient = useQueryClient();

  // Fetch company settings
  const { data: settings, isLoading: settingsLoading } = useQuery({
    queryKey: ['companySettings'],
    queryFn: () => tenantService.getCompanySettings(),
  });

  // Helper function to convert day number to day name
  const dayNumberToName = (dayNum: number): string => {
    const days = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    return days[dayNum];
  };

  // Helper function to convert day name to day number
  const dayNameToNumber = (dayName: string): number => {
    const days = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    return days.indexOf(dayName);
  };

  // Update forms when settings are loaded
  useEffect(() => {
    if (settings) {
      // Company form
      companyForm.setFieldsValue({
        name: settings.company.name,
        email: settings.company.email,
        phone: settings.company.phone,
        address: settings.company.address,
        city: settings.company.city,
        postal_code: settings.company.postal_code,
        country: settings.company.country,
        website: settings.company.website,
      });

      // Business form
      businessForm.setFieldsValue({
        working_hours_start: settings.business.working_hours?.start
          ? dayjs(settings.business.working_hours.start, 'HH:mm')
          : dayjs('08:00', 'HH:mm'),
        working_hours_end: settings.business.working_hours?.end
          ? dayjs(settings.business.working_hours.end, 'HH:mm')
          : dayjs('18:00', 'HH:mm'),
        working_days: settings.business.working_days?.map(dayNumberToName) || [],
      });

      // Financial form
      financialForm.setFieldsValue({
        default_currency: settings.business.currency || 'EUR',
      });
    }
  }, [settings, companyForm, businessForm, financialForm]);

  // Company update mutation
  const companyMutation = useMutation({
    mutationFn: (data: any) => tenantService.updateCompanySettings({ company: data }),
    onSuccess: (response: { message?: string }) => {
      message.success(response.message || 'Informations de l\'entreprise mises à jour');
      queryClient.invalidateQueries({ queryKey: ['companySettings'] });
    },
    onError: (error: any) => {
      message.error(error.response?.data?.error || 'Erreur lors de la mise à jour');
    },
  });

  // Business settings mutation
  const businessMutation = useMutation({
    mutationFn: (data: any) => tenantService.updateCompanySettings({ business: data }),
    onSuccess: (response: { message?: string }) => {
      message.success(response.message || 'Paramètres métier mis à jour');
      queryClient.invalidateQueries({ queryKey: ['companySettings'] });
    },
    onError: (error: any) => {
      message.error(error.response?.data?.error || 'Erreur lors de la mise à jour');
    },
  });

  const handleCompanyUpdate = async (values: any) => {
    await companyMutation.mutateAsync(values);
  };

  const handleBusinessUpdate = async (values: any) => {
    // Convert working hours from dayjs to string format
    const businessData = {
      working_hours: {
        start: values.working_hours_start?.format('HH:mm') || '08:00',
        end: values.working_hours_end?.format('HH:mm') || '18:00',
      },
      working_days: values.working_days?.map(dayNameToNumber) || [],
      currency: values.default_currency,
    };

    await businessMutation.mutateAsync(businessData);
  };

  const handleLogoUpload = (info: any) => {
    if (info.file) {
      message.success('Logo mis à jour');
    }
  };

  // Show loading spinner while fetching settings
  if (settingsLoading) {
    return (
      <div style={{ padding: '24px', textAlign: 'center' }}>
        <Spin size="large" />
      </div>
    );
  }

  const workingDaysOptions = [
    { label: 'Lundi', value: 'monday' },
    { label: 'Mardi', value: 'tuesday' },
    { label: 'Mercredi', value: 'wednesday' },
    { label: 'Jeudi', value: 'thursday' },
    { label: 'Vendredi', value: 'friday' },
    { label: 'Samedi', value: 'saturday' },
    { label: 'Dimanche', value: 'sunday' }
  ];

  return (
    <div style={{ padding: '24px' }}>
      <Row gutter={[24, 24]}>
        {/* Informations générales */}
        <Col xs={24} xl={16}>
          <Card
            title={
              <Space>
                <HomeOutlined />
                Informations de l'entreprise
              </Space>
            }
          >
            <Form
              form={companyForm}
              layout="vertical"
              onFinish={handleCompanyUpdate}
            >
              <Row gutter={16}>
                <Col xs={24} md={12}>
                  <Form.Item
                    name="name"
                    label="Nom commercial"
                    rules={[{ required: true, message: 'Le nom est requis' }]}
                  >
                    <Input placeholder="Nom de l'entreprise" />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item
                    name="legal_name"
                    label="Raison sociale"
                    rules={[{ required: true, message: 'La raison sociale est requise' }]}
                  >
                    <Input placeholder="Raison sociale" />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={16}>
                <Col xs={24} md={12}>
                  <Form.Item
                    name="siret"
                    label="SIRET"
                    rules={[{ required: true, message: 'Le SIRET est requis' }]}
                  >
                    <Input placeholder="12345678901234" />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item
                    name="vat_number"
                    label="Numéro de TVA"
                  >
                    <Input placeholder="FR12345678901" />
                  </Form.Item>
                </Col>
              </Row>

              <Form.Item
                name="description"
                label="Description"
              >
                <TextArea rows={3} placeholder="Description de l'entreprise" />
              </Form.Item>

              <Divider orientation="left">Coordonnées</Divider>

              <Form.Item
                name="address"
                label="Adresse"
                rules={[{ required: true, message: 'L\'adresse est requise' }]}
              >
                <Input placeholder="Adresse complète" />
              </Form.Item>

              <Row gutter={16}>
                <Col xs={24} md={8}>
                  <Form.Item
                    name="city"
                    label="Ville"
                    rules={[{ required: true, message: 'La ville est requise' }]}
                  >
                    <Input placeholder="Paris" />
                  </Form.Item>
                </Col>
                <Col xs={24} md={8}>
                  <Form.Item
                    name="postal_code"
                    label="Code postal"
                    rules={[{ required: true, message: 'Le code postal est requis' }]}
                  >
                    <Input placeholder="75001" />
                  </Form.Item>
                </Col>
                <Col xs={24} md={8}>
                  <Form.Item
                    name="country"
                    label="Pays"
                    rules={[{ required: true, message: 'Le pays est requis' }]}
                  >
                    <Select placeholder="Sélectionner un pays">
                      <Option value="France">France</Option>
                      <Option value="Belgium">Belgique</Option>
                      <Option value="Switzerland">Suisse</Option>
                      <Option value="Spain">Espagne</Option>
                    </Select>
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={16}>
                <Col xs={24} md={8}>
                  <Form.Item
                    name="phone"
                    label="Téléphone"
                    rules={[{ required: true, message: 'Le téléphone est requis' }]}
                  >
                    <Input prefix={<PhoneOutlined />} placeholder="+33123456789" />
                  </Form.Item>
                </Col>
                <Col xs={24} md={8}>
                  <Form.Item
                    name="email"
                    label="Email"
                    rules={[
                      { required: true, message: 'L\'email est requis' },
                      { type: 'email', message: 'Format email invalide' }
                    ]}
                  >
                    <Input prefix={<MailOutlined />} placeholder="contact@entreprise.fr" />
                  </Form.Item>
                </Col>
                <Col xs={24} md={8}>
                  <Form.Item
                    name="website"
                    label="Site web"
                  >
                    <Input prefix={<GlobalOutlined />} placeholder="https://entreprise.fr" />
                  </Form.Item>
                </Col>
              </Row>

              <Form.Item>
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={companyMutation.isPending}
                  icon={<SaveOutlined />}
                >
                  Sauvegarder les informations
                </Button>
              </Form.Item>
            </Form>
          </Card>
        </Col>

        {/* Logo et statut */}
        <Col xs={24} xl={8}>
          <Card title="Logo de l'entreprise">
            <div style={{ textAlign: 'center', marginBottom: '16px' }}>
              <img
                src="/api/placeholder/150/150"
                alt="Logo entreprise"
                style={{
                  width: '150px',
                  height: '150px',
                  objectFit: 'contain',
                  border: '1px solid #d9d9d9',
                  borderRadius: '8px'
                }}
              />
            </div>
            <Upload
              name="logo"
              showUploadList={false}
              beforeUpload={() => false}
              onChange={handleLogoUpload}
            >
              <Button icon={<UploadOutlined />} block>
                Changer le logo
              </Button>
            </Upload>
            
            <Divider />
            
            <Space direction="vertical" style={{ width: '100%' }}>
              <div>
                <Text strong>Statut: </Text>
                <Tag color="green">Actif</Tag>
              </div>
              <div>
                <Text strong>Plan: </Text>
                <Tag color="blue">Premium</Tag>
              </div>
              <div>
                <Text strong>Utilisateurs: </Text>
                <Text>12/50</Text>
              </div>
              <div>
                <Text strong>Véhicules: </Text>
                <Text>25/100</Text>
              </div>
            </Space>
          </Card>
        </Col>

        {/* Heures de travail */}
        <Col xs={24} xl={12}>
          <Card
            title={
              <Space>
                <ClockCircleOutlined />
                Heures de travail
              </Space>
            }
          >
            <Form
              form={businessForm}
              layout="vertical"
              onFinish={handleBusinessUpdate}
            >
              <Row gutter={16}>
                <Col xs={24} md={12}>
                  <Form.Item
                    name="working_hours_start"
                    label="Heure de début"
                  >
                    <TimePicker format="HH:mm" style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item
                    name="working_hours_end"
                    label="Heure de fin"
                  >
                    <TimePicker format="HH:mm" style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
              </Row>

              <Form.Item
                name="working_days"
                label="Jours de travail"
              >
                <Select
                  mode="multiple"
                  placeholder="Sélectionner les jours"
                  options={workingDaysOptions}
                  style={{ width: '100%' }}
                />
              </Form.Item>

              <Form.Item>
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={businessMutation.isPending}
                  icon={<SaveOutlined />}
                >
                  Sauvegarder
                </Button>
              </Form.Item>
            </Form>
          </Card>
        </Col>

        {/* Paramètres financiers */}
        <Col xs={24} xl={12}>
          <Card
            title={
              <Space>
                <DollarOutlined />
                Paramètres financiers
              </Space>
            }
          >
            <Form
              form={financialForm}
              layout="vertical"
              onFinish={handleBusinessUpdate}
            >
              <Form.Item
                name="default_currency"
                label="Devise par défaut"
              >
                <Select placeholder="Sélectionner une devise">
                  <Option value="EUR">Euro (EUR)</Option>
                  <Option value="USD">Dollar US (USD)</Option>
                  <Option value="GBP">Livre Sterling (GBP)</Option>
                </Select>
              </Form.Item>

              <Row gutter={16}>
                <Col xs={24} md={12}>
                  <Form.Item
                    name="fuel_price_per_liter"
                    label="Prix du carburant (€/L)"
                  >
                    <InputNumber
                      min={0}
                      step={0.01}
                      style={{ width: '100%' }}
                      placeholder="1.45"
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item
                    name="mileage_rate"
                    label="Indemnité kilométrique (€/km)"
                  >
                    <InputNumber
                      min={0}
                      step={0.01}
                      style={{ width: '100%' }}
                      placeholder="0.35"
                    />
                  </Form.Item>
                </Col>
              </Row>

              <Form.Item
                name="overtime_rate"
                label="Taux d'heures supplémentaires"
              >
                <InputNumber
                  min={1}
                  step={0.1}
                  style={{ width: '100%' }}
                  placeholder="1.5"
                  addonAfter="x"
                />
              </Form.Item>

              <Form.Item>
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={businessMutation.isPending}
                  icon={<SaveOutlined />}
                >
                  Sauvegarder
                </Button>
              </Form.Item>
            </Form>
          </Card>
        </Col>

        {/* Alertes et notifications */}
        <Col xs={24}>
          <Card
            title={
              <Space>
                <SafetyOutlined />
                Alertes et notifications
              </Space>
            }
          >
            <Alert
              message="Configuration des alertes automatiques"
              description="Définissez les seuils pour recevoir des alertes proactives sur la maintenance et les échéances."
              type="info"
              style={{ marginBottom: '16px' }}
            />

            <Form
              form={alertsForm}
              layout="vertical"
              onFinish={handleBusinessUpdate}
            >
              <Row gutter={16}>
                <Col xs={24} md={8}>
                  <Form.Item
                    name="maintenance_alert_km"
                    label="Alerte maintenance (km)"
                    tooltip="Nombre de kilomètres avant la prochaine maintenance pour déclencher une alerte"
                  >
                    <InputNumber
                      min={1000}
                      step={1000}
                      style={{ width: '100%' }}
                      placeholder="10000"
                      addonAfter="km"
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} md={8}>
                  <Form.Item
                    name="insurance_expiry_alert_days"
                    label="Alerte assurance (jours)"
                    tooltip="Nombre de jours avant expiration pour déclencher une alerte"
                  >
                    <InputNumber
                      min={1}
                      step={1}
                      style={{ width: '100%' }}
                      placeholder="30"
                      addonAfter="jours"
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} md={8}>
                  <Form.Item
                    name="fuel_alert_level"
                    label="Alerte carburant (%)"
                    tooltip="Niveau de carburant en pourcentage pour déclencher une alerte"
                  >
                    <InputNumber
                      min={1}
                      max={50}
                      step={1}
                      style={{ width: '100%' }}
                      placeholder="20"
                      addonAfter="%"
                    />
                  </Form.Item>
                </Col>
              </Row>

              <Form.Item>
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={businessMutation.isPending}
                  icon={<SaveOutlined />}
                >
                  Sauvegarder les alertes
                </Button>
              </Form.Item>
            </Form>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default AdminCompanySettings;